import _ from 'lodash';
import { Store } from '@/core/flux.service';
import type { PersistenceLevel } from '@/core/flux.service';
import type { ContextSchema } from '@/vantage/types/Context.types';

type VantageContextCategoryFilters = {
  kind: string[];
  extension: string[];
  seeqUser: string[];
};

interface VantageContextState {
  activeContext: ContextSchema[];
  pinnedExtensions: string[];
  contextSearchFilterText: string;
  contextCategoryFilters: VantageContextCategoryFilters;
  historyOpen: boolean;
}

export type VantageContextDehydratedState = Omit<VantageContextState, 'activeContext'>;

export class VantageContextStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqVantageContextStore';

  initialize(): void {
    const initialState: VantageContextState = {
      activeContext: [],
      pinnedExtensions: [],
      contextSearchFilterText: '',
      contextCategoryFilters: {
        kind: [],
        extension: [],
        seeqUser: [],
      },
      historyOpen: true,
    };
    this.state = this.immutable(initialState);
  }

  get contextSearchFilterText(): string {
    return this.state.get('contextSearchFilterText');
  }

  get contextCategoryFilters(): VantageContextCategoryFilters {
    return this.state.get('contextCategoryFilters');
  }

  get activeContext(): ContextSchema[] {
    return this.state.get('activeContext');
  }

  get pinnedExtensions(): string[] {
    return this.state.get('pinnedExtensions');
  }

  get historyOpen(): boolean {
    return this.state.get('historyOpen');
  }

  dehydrate(): VantageContextDehydratedState {
    return _.omit(this.state.serialize(), 'activeContext');
  }

  rehydrate(dehydratedState: Partial<VantageContextDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    SET_VANTAGE_ACTIVE_CONTEXT: (payload: { activeContext: ContextSchema[] }): void =>
      this.state.set('activeContext', payload.activeContext),
    SET_VANTAGE_PINNED_EXTENSIONS: (payload: { pinnedExtensions: string[] }): void =>
      this.state.set('pinnedExtensions', payload.pinnedExtensions),
    SET_VANTAGE_CONTEXT_SEARCH_FILTER: (payload: { contextSearchFilterText: string }): void =>
      this.state.set('contextSearchFilterText', payload.contextSearchFilterText),
    SET_VANTAGE_CONTEXT_CATEGORIES_FILTER: (payload: { contextCategoryFilters: VantageContextCategoryFilters }): void =>
      this.state.set('contextCategoryFilters', payload.contextCategoryFilters),
    SET_VANTAGE_TIMELINE_OPEN: (payload: { isOpen: boolean }): void => this.state.set('historyOpen', payload.isOpen),
  };
}
